import {
  Briefcase,
  BookOpenText,
  ScrollText,
  PenLine,
  GraduationCap,
  FlaskConical,
  Cpu,
  Scale,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "mba-project-help",
    icon: Briefcase,
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=900&q=60",
    name: "MBA Project Help",
    short: "End-to-end support for MBA projects — from topic selection and synopsis to the final bound report.",
    intro:
      "An MBA project is more than a submission; it is proof that you can convert classroom theory into business insight. We work alongside you at every stage so your project reads like the work of a practitioner, not a template.",
    included: [
      "Topic selection aligned with your specialisation and industry interest",
      "Synopsis and proposal drafting for guide approval",
      "Primary and secondary research design, including questionnaires",
      "Data analysis with clear interpretation of findings",
      "Complete report writing with formatting to your institute's guidelines",
      "Viva preparation notes and presentation slides on request",
    ],
    process: [
      "Share your specialisation, guidelines, and deadline over WhatsApp",
      "Receive a scoped plan with milestones and a fixed quote",
      "Review chapter-wise drafts and request unlimited scoped revisions",
      "Get the final report with a plagiarism check before submission",
    ],
    turnaround: "Typical delivery in 7–21 days depending on scope; express options available.",
    why: "Most MBA students come to us short on time, not ability. Many are working professionals juggling office hours with submission deadlines. We take the heavy lifting off your plate — the research, the writing, the formatting — while keeping you in control at every step, so the final report is something you understand fully and can defend confidently.",
  },
  {
    slug: "thesis-writing",
    icon: BookOpenText,
    image: "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=900&q=60",
    name: "Thesis Writing",
    short: "Structured, argument-driven thesis writing for master's candidates across disciplines.",
    intro:
      "A strong thesis is built on a defensible argument. We help you frame a precise research question, build a logically sequenced chapter structure, and write prose that examiners can follow without effort.",
    included: [
      "Research question refinement and thesis statement development",
      "Literature review with critical synthesis, not summary",
      "Methodology chapter with justification of chosen methods",
      "Results, discussion, and conclusion chapters written to flow as one argument",
      "Referencing in APA, MLA, Harvard, Chicago, or your required style",
      "Formatting to your university's thesis submission rules",
    ],
    process: [
      "Send your topic, guidelines, and any existing drafts",
      "Approve the proposed structure and milestone schedule",
      "Receive chapters progressively with time built in for your feedback",
      "Final proofread, plagiarism check, and formatting pass before handover",
    ],
    turnaround: "Full theses typically delivered in 3–6 weeks; individual chapters faster.",
    why: "A thesis fails when it reads like a stack of separate chapters instead of one argument. Our writers plan the whole document before writing a word, so every chapter pushes your central idea forward. You get clear, simple academic English that examiners can follow — and that you can explain in your defence without stress.",
  },
  {
    slug: "dissertation-writing",
    icon: ScrollText,
    image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=900&q=60",
    name: "Dissertation Writing",
    short: "Rigorous dissertation support from proposal defence to final submission.",
    intro:
      "Dissertations demand sustained rigour over months. Our role is to keep the research disciplined — a tight proposal, a defensible methodology, and analysis that actually answers the question you set out with.",
    included: [
      "Dissertation proposal with aims, objectives, and rationale",
      "Comprehensive literature review mapped to your research gaps",
      "Quantitative and qualitative methodology design",
      "Statistical analysis (SPSS, R, Excel) or thematic coding as required",
      "Findings and discussion chapters with academic depth",
      "Abstract, acknowledgements, appendices, and full formatting",
    ],
    process: [
      "Discuss your topic and institutional requirements with our team",
      "Lock the proposal and research design together",
      "Review each chapter as it is completed, with scoped revisions",
      "Receive the finished dissertation with an originality report",
    ],
    turnaround: "Standard dissertations in 4–8 weeks; proposals in 5–10 days.",
    why: "Dissertations run for months, and most problems come from weak planning at the start. We lock the proposal and method with you first, then deliver chapter by chapter so nothing drifts off course. If your guide asks for changes, we adjust quickly — that flexibility is why students stay with us to the final page.",
  },
  {
    slug: "academic-writing",
    icon: PenLine,
    image: "https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?auto=format&fit=crop&w=900&q=60",
    name: "Academic Writing",
    short: "Essays, assignments, case studies, and reports written to academic standards.",
    intro:
      "Not every task is a thesis, but every submission still carries marks. From reflective essays to case study analyses, we produce concise, well-referenced academic writing that meets your rubric point by point.",
    included: [
      "Essays, coursework, and assignment writing across subjects",
      "Case study analysis with frameworks applied correctly",
      "Reflective writing and reports in the required voice",
      "Critical reviews and annotated bibliographies",
      "Rubric-mapped structure so every criterion is addressed",
      "Full referencing and originality check on every piece",
    ],
    process: [
      "Send the brief, rubric, and deadline",
      "Confirm the quote and delivery date",
      "Receive the draft with time reserved for revision requests",
      "Submit with confidence after our final quality pass",
    ],
    turnaround: "Short pieces in 24–72 hours; longer coursework in 3–7 days.",
    why: "Small assignments decide a big share of your final grade, but they arrive constantly and pile up fast. We handle them quickly and properly — matched to your rubric, referenced correctly, and checked for originality — so you can save your energy for exams and the submissions that matter most.",
  },
  {
    slug: "phd-thesis-assistance",
    icon: GraduationCap,
    image: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?auto=format&fit=crop&w=900&q=60",
    name: "PhD Thesis Assistance",
    short: "Doctoral-level support: proposal, chapters, publication-ready papers, and viva prep.",
    intro:
      "Doctoral work is judged on contribution. We support PhD scholars with the parts that stall progress — sharpening the research gap, structuring dense chapters, and preparing manuscripts that survive review.",
    included: [
      "Research proposal and synopsis development for doctoral committees",
      "Chapter-wise writing support with scholarly depth",
      "Advanced statistical modelling and qualitative analysis support",
      "Journal manuscript preparation aligned to target journals",
      "Response-to-reviewer drafting for revisions",
      "Pre-submission review and viva question preparation",
    ],
    process: [
      "Book a detailed discussion of your research stage and blockers",
      "Agree on a support plan — full chapters or targeted assistance",
      "Work iteratively with a dedicated doctoral-level writer",
      "Close with an originality report and submission-ready formatting",
    ],
    turnaround: "Scoped per engagement; individual chapters typically 2–4 weeks.",
    why: "Doctoral work is lonely, and getting stuck is normal. Our PhD support pairs you with a writer who has been through the process and knows what committees look for. Whether you need one difficult chapter or end-to-end support from synopsis to submission, we work at your pace and keep your ideas at the centre.",
  },
  {
    slug: "research-paper-writing",
    icon: FlaskConical,
    image: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=900&q=60",
    name: "Research Paper Writing",
    short: "Publication-oriented research papers structured for journals and conferences.",
    intro:
      "A research paper lives or dies on clarity of contribution. We write papers with a sharp abstract, a positioned literature base, transparent methods, and results reported the way reviewers expect to see them.",
    included: [
      "Full paper drafting in IMRaD or your target structure",
      "Abstract and keyword optimisation for discoverability",
      "Literature positioning against current published work",
      "Figures, tables, and results presentation",
      "Journal formatting (IEEE, Springer, Elsevier styles and more)",
      "Plagiarism check with similarity report",
    ],
    process: [
      "Share your data, findings, or research concept",
      "Approve the paper outline and target format",
      "Review the full draft and request scoped revisions",
      "Receive the final paper formatted for submission",
    ],
    turnaround: "Most papers delivered in 7–14 days.",
    why: "Journals reject good research that is presented badly. We know what editors and reviewers look for — a sharp abstract, honest methods, and results shown clearly — and we build your paper around those expectations. That is why our papers tend to move through review with fewer rounds of correction.",
  },
  {
    slug: "academic-projects",
    icon: Cpu,
    image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=900&q=60",
    name: "Technical & Non-Technical Projects",
    short: "Complete academic projects — technical builds with documentation, and non-technical studies.",
    intro:
      "Whether your project needs working code with a report or a management study with field research, we deliver the complete package: the work itself, the documentation, and the presentation that explains it.",
    included: [
      "Technical projects with source code and implementation reports",
      "Non-technical projects: market studies, HR surveys, finance analyses",
      "Project reports formatted to institutional templates",
      "Diagrams, flowcharts, and system documentation",
      "PowerPoint decks for project presentations",
      "Guidance notes so you can explain every part of the work",
    ],
    process: [
      "Describe your project requirement and evaluation criteria",
      "Receive a feasibility note, plan, and quote",
      "Review working milestones — demos for technical, drafts for studies",
      "Final delivery with documentation and walkthrough support",
    ],
    turnaround: "Non-technical projects in 1–2 weeks; technical builds in 2–4 weeks.",
    why: "Project marks depend on two things: the work itself and how well you explain it. We deliver both — a working build or complete study, plus documentation and walkthrough notes written in simple language, so you can present the project as your own with full confidence in every question that follows.",
  },
  {
    slug: "law-thesis-writing",
    icon: Scale,
    image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=900&q=60",
    name: "Law Thesis Writing",
    short: "Doctrinal and empirical legal research writing with precise citation discipline.",
    intro:
      "Legal writing rewards precision. Our law-specialised writers handle doctrinal analysis, comparative studies, and empirical legal research with the citation discipline — OSCOLA, Bluebook, ILI — that law faculties demand.",
    included: [
      "Doctrinal and empirical legal research design",
      "Case law analysis and statutory interpretation sections",
      "Comparative legal studies across jurisdictions",
      "OSCOLA, Bluebook, and ILI citation formatting",
      "Dissertations, seminar papers, and moot memorials",
      "Plagiarism verification on all legal writing",
    ],
    process: [
      "Share your area of law, topic, and institutional guidelines",
      "Approve the issue framework and authorities list",
      "Review argument-wise drafts with revision windows",
      "Receive the final thesis with complete citations verified",
    ],
    turnaround: "Law theses typically in 3–5 weeks; shorter papers in 5–10 days.",
    why: "Legal writing has no room for loose language. Our law writers are trained in legal research and citation, so every authority is cited correctly and every argument is built the way law faculties expect. You get writing that is precise, well-supported, and ready for scrutiny.",
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
